import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

import { fetchAirshowJunctionArtifact } from "./airshowJunctionApi";
import type { AirshowJunctionMeshMetrics } from "./airshowJunctionContract";
import type {
  AirshowJunctionLayout,
  AirshowJunctionShading,
  AirshowJunctionViewerState,
} from "./airshowJunctionFeedback";
import { decodeAirshowGltf } from "./airshowComparisonGeometry";

export type { AirshowJunctionLayout, AirshowJunctionShading } from "./airshowJunctionFeedback";
export type AirshowJunctionCameraPreset = "front" | "side" | "top" | "isometric";

interface ViewerProps {
  modelId: string;
  baselineUrl: string | null;
  resultUrl: string | null;
  baselineMetrics: AirshowJunctionMeshMetrics | null;
  resultMetrics: AirshowJunctionMeshMetrics | null;
  loading: boolean;
  unavailableReason: string | null;
  layout: AirshowJunctionLayout;
  shading: AirshowJunctionShading;
  opacity: number;
  cameraCommand: { id: number; preset: AirshowJunctionCameraPreset };
  onViewStateChange: (state: AirshowJunctionViewerState) => void;
}

interface SideState {
  modelId: string | null;
  state: "loading" | "ready" | "unavailable";
  meshes: number;
  error: string | null;
}

interface Runtime {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  baseline: THREE.Group;
  result: THREE.Group;
  raf: number;
}

const EMPTY_SIDE: SideState = { modelId: null, state: "unavailable", meshes: 0, error: null };
const BASELINE_COLOR = 0x45b7d1;
const RESULT_COLOR = 0xf2ad55;
type MeshRole = "baseline" | "result";

function disposeGroup(group: THREE.Group): void {
  for (const child of [...group.children]) {
    group.remove(child);
    if (!(child instanceof THREE.Mesh)) continue;
    child.geometry.dispose();
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.forEach((material) => material.dispose());
  }
}

function sceneBox(runtime: Runtime): THREE.Box3 {
  const result = new THREE.Box3();
  let found = false;
  for (const group of [runtime.baseline, runtime.result]) {
    if (!group.children.length) continue;
    result.union(new THREE.Box3().setFromObject(group));
    found = true;
  }
  return found ? result : new THREE.Box3(
    new THREE.Vector3(-1, -1, -1),
    new THREE.Vector3(1, 1, 1),
  );
}

function placeCamera(runtime: Runtime, preset: AirshowJunctionCameraPreset): void {
  const box = sceneBox(runtime);
  const center = box.getCenter(new THREE.Vector3());
  const radius = Math.max(box.getSize(new THREE.Vector3()).length() * 0.5, 0.5);
  const directions: Record<AirshowJunctionCameraPreset, THREE.Vector3> = {
    front: new THREE.Vector3(-1, 0, 0),
    side: new THREE.Vector3(0, -1, 0),
    top: new THREE.Vector3(0, 0, 1),
    isometric: new THREE.Vector3(-0.75, -0.9, 0.55).normalize(),
  };
  runtime.camera.up.set(preset === "top" ? 1 : 0, 0, preset === "top" ? 0 : 1);
  runtime.controls.target.copy(center);
  runtime.camera.position.copy(center).add(directions[preset].multiplyScalar(radius * 2.8));
  runtime.camera.near = Math.max(radius / 100, 0.001);
  runtime.camera.far = Math.max(radius * 100, 100);
  runtime.camera.updateProjectionMatrix();
  runtime.controls.update();
}

function material(
  shading: AirshowJunctionShading,
  opacity: number,
  role: MeshRole,
): THREE.Material {
  const roleColor = role === "baseline" ? BASELINE_COLOR : RESULT_COLOR;
  const common = {
    opacity,
    transparent: opacity < 1,
    depthWrite: opacity >= 1,
    side: THREE.DoubleSide,
    wireframe: shading === "wireframe",
  };
  if (shading === "normals") {
    const normalMaterial = new THREE.MeshNormalMaterial(common);
    normalMaterial.onBeforeCompile = (shader) => {
      shader.uniforms.junctionTint = { value: new THREE.Color(roleColor) };
      shader.fragmentShader = shader.fragmentShader
        .replace("uniform float opacity;", "uniform float opacity; uniform vec3 junctionTint;")
        .replace(
          "gl_FragColor = vec4( normalize( normal ) * 0.5 + 0.5, diffuseColor.a );",
          "gl_FragColor = vec4( mix( normalize( normal ) * 0.5 + 0.5, junctionTint, 0.22 ), diffuseColor.a );",
        );
    };
    normalMaterial.customProgramCacheKey = () => `airshow-junction-normal-${role}`;
    return normalMaterial;
  }
  return new THREE.MeshStandardMaterial({
    ...common,
    color: roleColor,
    metalness: 0.04,
    roughness: 0.68,
  });
}

function updateMaterials(runtime: Runtime, shading: AirshowJunctionShading, opacity: number): void {
  for (const [group, role] of [
    [runtime.baseline, "baseline"],
    [runtime.result, "result"],
  ] as const) {
    group.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      const previous = Array.isArray(child.material) ? child.material : [child.material];
      child.material = material(shading, opacity, role);
      child.renderOrder = role === "baseline" ? 1 : 2;
      previous.forEach((item) => item.dispose());
    });
  }
  runtime.renderer.domElement.dataset.symmetricOpacity = opacity.toFixed(2);
  runtime.renderer.domElement.dataset.symmetricShading = shading;
}

function addParts(group: THREE.Group, source: unknown, role: MeshRole): number {
  const parts = decodeAirshowGltf(source);
  parts.forEach((part) => {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(part.positions, 3));
    geometry.setIndex(new THREE.BufferAttribute(part.indices, 1));
    geometry.computeVertexNormals();
    const mesh = new THREE.Mesh(geometry, material("solid", 1, role));
    mesh.name = part.name;
    mesh.renderOrder = role === "baseline" ? 1 : 2;
    group.add(mesh);
  });
  return parts.length;
}

function metricLabel(metrics: AirshowJunctionMeshMetrics | null): string {
  return metrics
    ? `${metrics.vertices.toLocaleString()} vertices · ${metrics.triangles.toLocaleString()} triangles`
    : "No mesh metrics";
}

function cameraSignature(runtime: Runtime): string {
  return [
    runtime.camera.position.x, runtime.camera.position.y, runtime.camera.position.z,
    runtime.controls.target.x, runtime.controls.target.y, runtime.controls.target.z,
  ].map((value) => value.toFixed(4)).join("|");
}

export function AirshowJunctionViewer({
  modelId,
  baselineUrl,
  resultUrl,
  baselineMetrics,
  resultMetrics,
  loading,
  unavailableReason,
  layout,
  shading,
  opacity,
  cameraCommand,
  onViewStateChange,
}: ViewerProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const runtimeRef = useRef<Runtime | null>(null);
  const displayRef = useRef({ layout, shading, opacity, cameraPreset: cameraCommand.preset });
  const [baselineState, setBaselineState] = useState<SideState>(EMPTY_SIDE);
  const [resultState, setResultState] = useState<SideState>(EMPTY_SIDE);
  const [cameraSync, setCameraSync] = useState({ signature: "camera|pending", revision: 0 });
  const recordCamera = useCallback((runtime: Runtime) => setCameraSync((current) => {
    const signature = cameraSignature(runtime);
    return current.signature === signature ? current : {
      signature, revision: current.revision + 1,
    };
  }), []);
  displayRef.current = { layout, shading, opacity, cameraPreset: cameraCommand.preset };

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0x11171d, 1);
    const scene = new THREE.Scene();
    scene.add(new THREE.HemisphereLight(0xffffff, 0x24313b, 1.6));
    const key = new THREE.DirectionalLight(0xffffff, 2.2);
    key.position.set(-2, -3, 5);
    scene.add(key);
    const baseline = new THREE.Group();
    const result = new THREE.Group();
    scene.add(baseline, result);
    const camera = new THREE.PerspectiveCamera(42, 1, 0.01, 5000);
    camera.up.set(0, 0, 1);
    const controls = new OrbitControls(camera, canvas);
    controls.enableDamping = true;
    const runtime: Runtime = { renderer, scene, camera, controls, baseline, result, raf: 0 };
    runtimeRef.current = runtime;
    const emitCamera = () => recordCamera(runtime);
    controls.addEventListener("change", emitCamera);
    const resize = () => renderer.setSize(
      Math.max(canvas.clientWidth, 1), Math.max(canvas.clientHeight, 1), false,
    );
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    resize();
    const draw = () => {
      controls.update();
      const width = renderer.domElement.width;
      const height = renderer.domElement.height;
      renderer.setScissorTest(true);
      if (displayRef.current.layout === "split") {
        const half = Math.floor(width / 2);
        camera.aspect = Math.max(half, 1) / Math.max(height, 1);
        camera.updateProjectionMatrix();
        baseline.visible = true;
        result.visible = false;
        renderer.setViewport(0, 0, half, height);
        renderer.setScissor(0, 0, half, height);
        renderer.render(scene, camera);
        baseline.visible = false;
        result.visible = true;
        renderer.setViewport(half, 0, width - half, height);
        renderer.setScissor(half, 0, width - half, height);
        renderer.render(scene, camera);
      } else {
        camera.aspect = Math.max(width, 1) / Math.max(height, 1);
        camera.updateProjectionMatrix();
        baseline.visible = true;
        result.visible = true;
        renderer.setViewport(0, 0, width, height);
        renderer.setScissor(0, 0, width, height);
        renderer.render(scene, camera);
      }
      runtime.raf = requestAnimationFrame(draw);
    };
    placeCamera(runtime, "isometric");
    emitCamera();
    draw();
    return () => {
      cancelAnimationFrame(runtime.raf);
      observer.disconnect();
      controls.removeEventListener("change", emitCamera);
      controls.dispose();
      disposeGroup(baseline);
      disposeGroup(result);
      renderer.dispose();
      runtimeRef.current = null;
    };
  }, [recordCamera]);

  useLayoutEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    const controller = new AbortController();
    disposeGroup(runtime.baseline);
    disposeGroup(runtime.result);
    const empty: SideState = {
      modelId, state: loading ? "loading" : "unavailable", meshes: 0, error: null,
    };
    setBaselineState(empty);
    setResultState(empty);
    if (!baselineUrl || !resultUrl) return () => controller.abort();
    setBaselineState({ modelId, state: "loading", meshes: 0, error: null });
    setResultState({ modelId, state: "loading", meshes: 0, error: null });
    const load = async (
      url: string,
      group: THREE.Group,
      role: MeshRole,
      setter: (state: SideState) => void,
    ) => {
      try {
        const meshCount = addParts(
          group,
          await fetchAirshowJunctionArtifact(url, controller.signal),
          role,
        );
        if (controller.signal.aborted) return;
        setter({ modelId, state: "ready", meshes: meshCount, error: null });
        updateMaterials(runtime, displayRef.current.shading, displayRef.current.opacity);
        placeCamera(runtime, displayRef.current.cameraPreset);
        recordCamera(runtime);
      } catch (error) {
        if (controller.signal.aborted || (error instanceof Error && error.name === "AbortError")) return;
        disposeGroup(group);
        setter({
          modelId,
          state: "unavailable",
          meshes: 0,
          error: error instanceof Error ? error.message : "Junction mesh artifact is unavailable",
        });
      }
    };
    void load(baselineUrl, runtime.baseline, "baseline", setBaselineState);
    void load(resultUrl, runtime.result, "result", setResultState);
    return () => controller.abort();
  }, [baselineUrl, loading, modelId, recordCamera, resultUrl]);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (runtime) updateMaterials(runtime, shading, opacity);
  }, [opacity, shading]);

  useLayoutEffect(() => {
    const runtime = runtimeRef.current;
    if (runtime) {
      placeCamera(runtime, cameraCommand.preset);
      recordCamera(runtime);
    }
  }, [cameraCommand, recordCamera]);

  const currentBaseline = baselineState.modelId === modelId ? baselineState : {
    modelId, state: "loading" as const, meshes: 0, error: null,
  };
  const currentResult = resultState.modelId === modelId ? resultState : {
    modelId, state: "loading" as const, meshes: 0, error: null,
  };
  useEffect(() => {
    onViewStateChange({
      modelId,
      layout,
      shading,
      symmetricOpacity: opacity,
      cameraSync: { synchronized: true, ...cameraSync },
      artifacts: {
        baseline: { status: currentBaseline.state, renderedMeshCount: currentBaseline.meshes },
        result: { status: currentResult.state, renderedMeshCount: currentResult.meshes },
      },
    });
  }, [cameraCommand.id, cameraSync, currentBaseline.meshes, currentBaseline.state,
    currentResult.meshes, currentResult.state, layout, modelId, onViewStateChange,
    opacity, shading]);

  return (
    <div
      className="airshow-junction-viewer"
      data-layout={layout}
      data-loading={String(loading)}
      data-model-id={modelId}
      data-testid="openvsp-junction-viewer"
    >
      <canvas
        aria-label="Synchronized baseline and junction result"
        data-baseline-color="#45b7d1"
        data-result-color="#f2ad55"
        data-symmetric-opacity={opacity.toFixed(2)}
        data-symmetric-shading={shading}
        data-testid="openvsp-junction-canvas"
        ref={canvasRef}
      />
      <div
        className="airshow-junction-viewport is-baseline"
        data-camera-signature={cameraSync.signature}
        data-camera-revision={cameraSync.revision}
        data-render-state={currentBaseline.state}
        data-rendered-mesh-count={currentBaseline.meshes}
        data-testid="openvsp-junction-viewport-baseline"
      >
        <header><span><i aria-hidden="true" />Baseline</span><strong>{metricLabel(baselineMetrics)}</strong></header>
        {currentBaseline.state === "unavailable" ? <p>{currentBaseline.error ?? unavailableReason ?? "Baseline unavailable"}</p> : null}
      </div>
      <div
        className="airshow-junction-viewport is-result"
        data-camera-signature={cameraSync.signature}
        data-camera-revision={cameraSync.revision}
        data-render-state={currentResult.state}
        data-rendered-mesh-count={currentResult.meshes}
        data-testid="openvsp-junction-viewport-result"
      >
        <header><span><i aria-hidden="true" />Diagnostic result</span><strong>{metricLabel(resultMetrics)}</strong></header>
        {currentResult.state === "unavailable" ? <p>{currentResult.error ?? unavailableReason ?? "Result unavailable"}</p> : null}
      </div>
    </div>
  );
}
